import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import type { Page } from '@/types/backend';

/**
 * Tiny pager that consumes Spring's Page<T> envelope. We deliberately
 * avoid jumping to arbitrary page numbers — admin UX rarely needs it,
 * and showing "page 47 of 213" tempts operators to drill into noise.
 * Operators who need a specific time slice should refine the filters.
 */
export function Pagination<T>({ page, onChange }: { page: Page<T> | undefined; onChange: (next: number) => void }) {
  const theme = useTheme();
  if (!page) return null;
  const start = page.numberOfElements === 0 ? 0 : page.number * page.size + 1;
  const end = page.number * page.size + page.numberOfElements;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mt: 2, color: theme.palette.text.secondary }}
    >
      <Typography variant="caption">
        {page.totalElements === 0
          ? 'No rows.'
          : `${start.toLocaleString()}–${end.toLocaleString()} of ${page.totalElements.toLocaleString()}`}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" disabled={page.first} onClick={() => onChange(page.number - 1)} aria-label="previous page">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="caption" sx={{ px: 1 }}>
          Page {page.number + 1} / {Math.max(page.totalPages, 1)}
        </Typography>
        <IconButton size="small" disabled={page.last} onClick={() => onChange(page.number + 1)} aria-label="next page">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  );
}
